/**
 * Header row for the CoverageDetails page: patient name + MRN on the left,
 * the Report Inaccuracy / Validate / Refresh actions on the right.
 *
 * Purely presentational - all analytics dispatch and modal state changes are
 * delegated to the parent via the supplied callbacks.
 */
import React from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";
import RefreshIcon from "../../../../svg/refresh-icon";

/** Props controlling header identity and actions for CoverageDetails. */
interface CoverageDetailsHeaderProps {
  patientFullName: string;
  patientIdentifier: string;
  /** True when any returned coverage row was manually edited by a user. */
  isManuallyUpdated: boolean;
  isRefreshingCoverage: boolean;
  onReportInaccuracyClick: () => void;
  onValidateClick: () => void;
  onRefreshClick: () => void;
}

/**
 * Renders patient identity and coverage actions for the CoverageDetails page.
 *
 * @param props - Header labels, manual-update flag, and action handlers
 * @returns Header row JSX for coverage details
 */
export const CoverageDetailsHeader: React.FC<CoverageDetailsHeaderProps> = ({
  patientFullName,
  patientIdentifier,
  isManuallyUpdated,
  isRefreshingCoverage,
  onReportInaccuracyClick,
  onValidateClick,
  onRefreshClick,
}) => {
  return (
    <div className="coverage-details--main-header flex items-center justify-between gap-1.5 sm:gap-2">
      <div className="coverage-details--header-details flex items-center justify-center gap-1">
        <div className="coverage-details--patient-name text-h11 font-bold leading-5 text-black">
          {patientFullName}
        </div>
        <div className="coverage-details--page-name text-sm font-semiBold text-primaryGray-2">
          ({patientIdentifier})
        </div>
        {isManuallyUpdated && (
          <div className="rounded bg-[#E6F7EE] px-2 py-0.5 text-xs font-semibold text-[#0D8A4F]">
            Manually Updated
          </div>
        )}
      </div>
      <div className="manual-visit-type-and-cpt-container flex shrink-0 items-center gap-1.5 sm:gap-2 md:gap-3">
        {!isManuallyUpdated && (
          <div
            className="authorization--header__mark-error text-small font-semibold text-[#CD0202] hover:cursor-pointer hover:text-red-500"
            onClick={onReportInaccuracyClick}
          >
            Report Inaccuracy
          </div>
        )}
        <div
          className="authorization--header__mark-error text-small font-semibold text-[#0056D6] hover:cursor-pointer hover:text-blue-500"
          onClick={onValidateClick}
        >
          Validate
        </div>

        {!isManuallyUpdated && (
          <div
            className="refresh-icon flex h-[1.375rem] w-[1.375rem] items-center justify-center rounded-[0.1375rem] bg-[#F7F9FA] hover:scale-105 hover:cursor-pointer"
            onClick={() => {
              if (!isRefreshingCoverage) {
                onRefreshClick();
              }
            }}
          >
            {isRefreshingCoverage ? <SpinningLoader /> : <RefreshIcon />}
          </div>
        )}
      </div>
    </div>
  );
};
